import { createSlice } from "@reduxjs/toolkit";

// Note: Tokens are now stored in HttpOnly cookies, not in Redux
const initialState = {
  user: null,
  company: null,
  isAuthenticated: false,
  // True while a system admin is viewing a company's client side as a test member
  isViewingAsClient: false,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setCredentials: (state, action) => {
      const { user, company } = action.payload;
      state.user = user;
      state.company = company || user?.company || null;
      state.isAuthenticated = true;
      // Note: tokens are now in HttpOnly cookies, not stored in Redux
    },
    logout: (state) => {
      state.user = null;
      state.company = null;
      state.isAuthenticated = false;
      state.isViewingAsClient = false;
    },
    // Admin enters a company's client side: swap identity to the test member
    setViewAs: (state, action) => {
      const { user, company } = action.payload;
      state.user = user;
      state.company = company || user?.company || null;
      state.isAuthenticated = true;
      state.isViewingAsClient = true;
    },
    // Admin exits client view (the restored session is applied via setCredentials)
    clearViewAs: (state) => {
      state.isViewingAsClient = false;
    },
    updateUser: (state, action) => {
      state.user = { ...state.user, ...action.payload };
    },
    updateCompany: (state, action) => {
      state.company = { ...state.company, ...action.payload };
    },
  },
});

export const { setCredentials, logout, updateUser, updateCompany, setViewAs, clearViewAs } = authSlice.actions;

export default authSlice.reducer;

// Selectors
export const selectCurrentUser = (state) => state.auth.user;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
// Note: Token selectors removed - tokens are now in HttpOnly cookies

// Company selectors
export const selectCurrentCompany = (state) => state.auth.company;
export const selectCompanyId = (state) => state.auth.company?.id;
export const selectCompanyName = (state) => state.auth.company?.name;
export const selectCompanySlug = (state) => state.auth.company?.slug;
export const selectInviteCode = (state) => state.auth.company?.inviteCode;

// Role-based selectors
export const selectUserRole = (state) => state.auth.user?.role;
export const selectUserRoleName = (state) => state.auth.user?.role?.name;
export const selectUserRoleLevel = (state) => state.auth.user?.role?.level;
export const selectUserPermissions = (state) => state.auth.user?.role?.permissions || [];
export const selectIsAdmin = (state) => (state.auth.user?.role?.level || 0) >= 4;
// Staff-or-above (system role level >= 3). Mirrors the backend `requireMinRole(3)`
// guard on the diagnostic approve/assign endpoints, so the UI gate matches the API.
export const selectIsStaffOrAbove = (state) => (state.auth.user?.role?.level || 0) >= 3;

// Company role selectors
export const selectCompanyRole = (state) => state.auth.user?.companyRole;
export const selectIsOwner = (state) => state.auth.user?.companyRole === 'owner';
export const selectIsCompanyAdmin = (state) => {
  const role = state.auth.user?.companyRole;
  return role === 'owner' || role === 'admin';
};
export const selectHasCompany = (state) => !!state.auth.company;
export const selectIsCompanyActive = (state) => state.auth.company?.isActive !== false;

// Admin "view client side" selector
export const selectIsViewingAsClient = (state) => state.auth.isViewingAsClient === true;
